import { AdderValue, CustomColumnHeader, ID } from 'types';

interface ResolveAdderContributionParams {
  extendedPrice?: number | string | null;
  header: CustomColumnHeader;
  pricePerUnit?: number | string | null;
  quantityOfUnits: number | string | null | undefined;
  supplierProvidedValue?: number | string | null;
}

interface ResolveSavedAdderContributionParams {
  adderValue: AdderValue;
  extendedPrice?: number | string | null;
  header: CustomColumnHeader;
  pricePerUnit?: number | string | null;
  quantityOfUnits: number | string | null | undefined;
}

const toFiniteNumber = (value: number | string | null | undefined): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Resolves a supplier-entered adder value to its monetary contribution.
 *
 * Per-line adders are already monetary amounts. A custom multiplier's supplier input is
 * a rate applied against an operand -- a percentage scalar against Price Per Unit
 * (PEPPERMINT-2897) or against the whole line's Extended Price, or a dollar rate
 * against Quantity of Units (POLARIS-2925/2926). The rate itself is never currency-converted;
 * callers provide the operand in the currency/units used by the surrounding total.
 */
export const isPercentageMultiplierAdder = (header?: CustomColumnHeader): boolean => {
  const config = header?.adder_column_config;
  return config?.scope === 'custom_multiplier' &&
    config.rate_type === 'percentage' &&
    (config.multiplier_operand_source === 'price_per_unit' ||
      config.multiplier_operand_source === 'extended_price');
};

/** The subset of percentage adders whose base is the whole line rather than the unit price. */
export const isExtendedPriceMultiplierAdder = (header?: CustomColumnHeader): boolean => {
  const config = header?.adder_column_config;
  return config?.scope === 'custom_multiplier' &&
    config.multiplier_operand_source === 'extended_price' &&
    config.rate_type === 'percentage';
};

export const isQuantityOfUnitsDollarRateAdder = (header?: CustomColumnHeader): boolean => {
  const config = header?.adder_column_config;
  return config?.scope === 'custom_multiplier' &&
    config.multiplier_operand_source === 'quantity_of_units' &&
    config.rate_type === 'price';
};

export const isRateBasedMultiplierAdder = (header?: CustomColumnHeader): boolean =>
  isPercentageMultiplierAdder(header) || isQuantityOfUnitsDollarRateAdder(header);

export const resolveSavedAdderInput = (
  header: CustomColumnHeader | undefined,
  adderValue: AdderValue
): number => {
  if (header && isRateBasedMultiplierAdder(header)) {
    return Number(adderValue.supplier_provided_rate_value ?? 0);
  }

  return Number(adderValue.supplier_provided_value ?? 0);
};

export const buildAdderValuePayload = (
  header: CustomColumnHeader | undefined,
  headerId: ID,
  value: number
): {
  custom_column_header_id: ID;
  supplier_provided_rate_value?: number;
  supplier_provided_value?: number;
} => isRateBasedMultiplierAdder(header)
  ? { custom_column_header_id: headerId, supplier_provided_rate_value: value }
  : { custom_column_header_id: headerId, supplier_provided_value: value };

/**
 * Resolves a supplier-entered adder value to its monetary contribution. Operand selection mirrors
 * ResponseLineItemAdderValues::Calculator#operand_value.
 */
export const resolveAdderContribution = ({
  header,
  extendedPrice,
  pricePerUnit,
  quantityOfUnits,
  supplierProvidedValue,
}: ResolveAdderContributionParams): number => {
  const supplierValue = toFiniteNumber(supplierProvidedValue);

  if (isQuantityOfUnitsDollarRateAdder(header)) {
    return toFiniteNumber(quantityOfUnits) * supplierValue;
  }

  if (!isPercentageMultiplierAdder(header)) return supplierValue;

  const base = isExtendedPriceMultiplierAdder(header)
    ? toFiniteNumber(extendedPrice)
    : toFiniteNumber(pricePerUnit);

  return base * supplierValue / 100;
};

/**
 * Resolves a persisted adder value to its contribution.
 *
 * A percentage adder's `supplier_provided_value` is a save-time snapshot in the then-current
 * quoting currency, so it goes stale as soon as the supplier switches currency --
 * recalculateOnCurrencyChange deliberately does not convert percentage adders, since the overlay
 * slot holds the rate, not money. So when a scalar is present it is the source of truth and the
 * contribution is recomputed from scalar x current base. Rails writes the same figure unrounded
 * (Calculator#supplier_value; only the event-currency `value` is floored), so this agrees with the
 * server rather than drifting from it.
 *
 * A percentage row can carry money WITHOUT a scalar, though -- a legacy row, or one saved before
 * the header became a multiplier. Recomputing those from an absent scalar would resolve them to
 * $0, so they keep their persisted money, same as flat, per-line and dollar-rate adders.
 */
export const resolveSavedAdderContribution = ({
  adderValue,
  header,
  extendedPrice,
  pricePerUnit,
  quantityOfUnits,
}: ResolveSavedAdderContributionParams): number => {
  const hasRateScalar = adderValue.supplier_provided_rate_value !== null &&
    adderValue.supplier_provided_rate_value !== undefined;

  if (
    !(isPercentageMultiplierAdder(header) && hasRateScalar) &&
    adderValue.supplier_provided_value !== null &&
    adderValue.supplier_provided_value !== undefined
  ) {
    return toFiniteNumber(adderValue.supplier_provided_value);
  }

  return resolveAdderContribution({
    extendedPrice,
    header,
    pricePerUnit,
    quantityOfUnits,
    supplierProvidedValue: resolveSavedAdderInput(header, adderValue),
  });
};
